import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  ChangeEventHandler
} from 'react';
import {Autocomplete, ListBoxOptionType} from '@flixbus/honeycomb-react';
import {useAppSelector} from '@/Pages/Common/hooks/useAppSelector';
import {LineAutocompleteFieldProps} from './LineAutocompleteField.types';

const LineAutocompleteField: React.FC<LineAutocompleteFieldProps> = ({
  line,
  valid,
  onSelected
}) => {
  const lineSchedules = useAppSelector(
    ({productionCost}) => productionCost.lineSchedules
  );
  const lines = useAppSelector(({productionCost}) => productionCost.lines);

  const [name, setName] = useState(line ?? '');
  const [filteredOptions, setFilteredOptions] = useState<ListBoxOptionType[]>(
    []
  );
  const [loading, setLoading] = useState(false);

  const lineOptions: ListBoxOptionType[] = useMemo(
    () =>
      lines
        .filter(
          line =>
            !lineSchedules.some(
              lineSchedule =>
                line.uuid === lineSchedule.lineUuid &&
                lineSchedule.lineNumber !== name
            )
        )
        .map(line => ({
          title: line.number ?? '',
          value: line.uuid
        }))
        .sort((l1, l2) => l1.title.localeCompare(l2.title)),
    [lineSchedules, lines, name]
  );

  const handleChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    e => {
      setLoading(true);
      setName(e.target.value);
    },
    [setLoading, setName]
  );

  const handleDebounce = useCallback(
    async (inputValue: string) => {
      const searchedText = inputValue.toLowerCase();

      if (!searchedText) {
        setFilteredOptions([]);
        setLoading(false);
        return [];
      }

      setLoading(true);
      const filteredLines = lineOptions.filter(line =>
        line.title.toLowerCase().startsWith(searchedText)
      );
      setFilteredOptions(filteredLines);
      setLoading(false);

      return filteredLines;
    },
    [lineOptions]
  );

  const handleSelect = useCallback(
    (selectedOptions: ListBoxOptionType[]) => {
      if (selectedOptions.length === 0) return;

      setFilteredOptions([]);
      setName(selectedOptions[0].title);
      onSelected(selectedOptions[0]);
    },
    [onSelected]
  );

  const renderOption = useCallback(
    (option: ListBoxOptionType) => option.title,
    []
  );

  useEffect(() => setName(line ?? ''), [line]);

  return (
    <Autocomplete
      comboboxProps={{
        label: 'Line number*',
        loading,
        infoError: 'Cannot be empty',
        valid,
        onChange: handleChange
      }}
      options={filteredOptions}
      inputValue={name}
      listBoxProps={{
        optionsToDisplay: 5,
        optionProps: (option: ListBoxOptionType) => ({
          renderOption: () => renderOption(option)
        })
      }}
      onDebounce={handleDebounce}
      onChange={handleSelect}
    />
  );
};

export default React.memo(LineAutocompleteField);
